/**
 * Cross-subdomain "this browser has an app session" presence flag.
 *
 * The marketing landing page (agapae.gr) and the app (app.agapae.gr) are
 * separate origins, so the landing page cannot read the app's auth tokens
 * (those live in app.agapae.gr's localStorage). To let the landing page show
 * "Sign in" vs "Register" for a returning user *on the same browser*, the app
 * writes a small flag cookie on the shared parent domain (.agapae.gr) when a
 * session starts and clears it on logout.
 *
 * Important:
 * - This is a NON-SENSITIVE presence flag only. Never store a token here — it
 *   is intentionally JS-readable (not httpOnly) so the static landing page can
 *   read it, which makes it unsuitable for anything secret. Real auth continues
 *   to happen in the app via the localStorage JWTs.
 * - The parent-domain cookie only works across real subdomains, so it is a
 *   no-op on localhost (dev landing/app are different ports with no shared
 *   parent). The landing page treats "no cookie" as logged-out, which is the
 *   correct, safe default for dev.
 */

const COOKIE_NAME = 'agapae_session';

// Shared registrable domain in production. A cookie scoped to ".agapae.gr" is
// visible to agapae.gr, www.agapae.gr and app.agapae.gr alike.
const SHARED_DOMAIN_SUFFIX = 'agapae.gr';

// 30 days — comfortably longer than a typical "did I sign up here?" memory
// window, and harmless since it carries no auth weight.
const MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

/**
 * Returns the `; domain=.agapae.gr` attribute when running on a production
 * agapae.gr host, or an empty string otherwise (localhost / previews), in
 * which case the cookie falls back to a host-only scope.
 */
function domainAttribute(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host === SHARED_DOMAIN_SUFFIX || host.endsWith(`.${SHARED_DOMAIN_SUFFIX}`)) {
    return `; domain=.${SHARED_DOMAIN_SUFFIX}`;
  }
  return '';
}

/** Only mark the cookie Secure over HTTPS; on http://localhost it would be dropped. */
function secureAttribute(): string {
  if (typeof window === 'undefined') return '';
  return window.location.protocol === 'https:' ? '; secure' : '';
}

/** Set the presence flag (call when a real user session is established). */
export function setSessionFlagCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${COOKIE_NAME}=1; path=/; max-age=${MAX_AGE_SECONDS}; samesite=lax` +
    domainAttribute() +
    secureAttribute();
}

/** Clear the presence flag (call on logout). */
export function clearSessionFlagCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${COOKIE_NAME}=; path=/; max-age=0; samesite=lax` + domainAttribute() + secureAttribute();
}
